import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle } from "lucide-react";

const SECTION_OPTIONS = [
  ["194A", "194A - Interest (other than securities)"],
  ["194B", "194B - Lottery/Crossword"],
  ["194C", "194C - Contractors"],
  ["194D", "194D - Insurance Commission"],
  ["194DA", "194DA - Life Insurance Payout"],
  ["194H", "194H - Commission/Brokerage"],
  ["194I(a)", "194I(a) - Rent (Plant & Machinery) - 2%"],
  ["194I(b)", "194I(b) - Rent (Building/Land) - 10%"],
  ["194IA", "194IA - Property Transfer"],
  ["194IB", "194IB - Rent by Individual/HUF"],
  ["194J", "194J - Professional/Technical Services"],
  ["194K", "194K - Mutual Fund Income"],
  ["194N", "194N - Cash Withdrawal"],
  ["194O", "194O - E-commerce Operator"],
  ["194Q", "194Q - Purchase of Goods"],
  ["194R", "194R - Benefits/Perquisites"],
  ["194S", "194S - Virtual Digital Assets"],
  ["194T", "194T - Payment to Partners"],
  ["195", "195 - Non-Resident Payments"],
  ["206C(1)", "206C(1) - TCS on Sales"],
  ["206C(1H)", "206C(1H) - TCS on Sale of Goods"],
  ["206C(1G)", "206C(1G) - TCS on Overseas Tour"],
  ["206C(1F)", "206C(1F) - TCS on Motor Vehicle"],
  ["206CA", "206CA - TCS Alcoholic Liquor"],
  ["206CB", "206CB - TCS Timber/Tendu"],
  ["206CC", "206CC - TCS Scrap"],
  ["206CD", "206CD - TCS Minerals"],
];

const RateChoice194C = ({ rateChoice, setRateChoice }) => (
  <div className="p-3 bg-blue-50 rounded-lg">
    <Label className="text-sm font-medium text-blue-800">194C Rate Options</Label>
    <p className="text-xs text-blue-600 mb-2">Rate will be determined by each party's PAN entity type, or you can override:</p>
    <div className="mt-2 space-y-2">
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194c" value="" checked={rateChoice === ""} onChange={() => setRateChoice("")} />
        <span className="text-sm">Auto (based on PAN)</span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194c" value="1" checked={rateChoice === "1"} onChange={() => setRateChoice("1")} />
        <span className="text-sm">1% (Individual/HUF)</span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194c" value="2" checked={rateChoice === "2"} onChange={() => setRateChoice("2")} />
        <span className="text-sm">2% (Company/Others)</span>
      </label>
    </div>
  </div>
);

const RateChoice194I = ({ rateChoice, setRateChoice }) => (
  <div className="p-3 bg-orange-50 rounded-lg">
    <Label className="text-sm font-medium text-orange-800">194I Rent Rate Options</Label>
    <p className="text-xs text-orange-600 mb-2">Choose based on nature of rent:</p>
    <div className="mt-2 space-y-2">
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194i" value="10" checked={rateChoice === "10" || rateChoice === ""} onChange={() => setRateChoice("10")} />
        <span className="text-sm"><strong>10%</strong> - Building/Land/Furniture (194I(b)) - <em>Default</em></span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194i" value="2" checked={rateChoice === "2"} onChange={() => setRateChoice("2")} />
        <span className="text-sm"><strong>2%</strong> - Plant & Machinery (194I(a))</span>
      </label>
    </div>
  </div>
);

const RateChoice194J = ({ rateChoice, setRateChoice }) => (
  <div className="p-3 bg-purple-50 rounded-lg">
    <Label className="text-sm font-medium text-purple-800">194J Rate Options</Label>
    <div className="mt-2 space-y-2">
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194j" value="professional" checked={rateChoice === "professional" || rateChoice === ""} onChange={() => setRateChoice("professional")} />
        <span className="text-sm">10% (Professional Fees)</span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name="rate194j" value="technical" checked={rateChoice === "technical"} onChange={() => setRateChoice("technical")} />
        <span className="text-sm">2% (Technical Services/FTS/Call Center)</span>
      </label>
    </div>
  </div>
);

export const SectionEditModal = ({
  sectionModal,
  setSectionModal,
  newSection,
  setNewSection,
  rateChoice,
  setRateChoice,
  handleSectionUpdate,
  selectedTxns,
}) => {
  const close = () => setSectionModal({ open: false, txn: null, isBulk: false });
  const is194I = newSection === "194I(a)" || newSection === "194I(b)" || newSection?.startsWith("194I");

  return (
    <Dialog open={sectionModal.open} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {sectionModal.isBulk ? `Bulk Assign Section (${selectedTxns.size} transactions)` : "Update TDS Section"}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {!sectionModal.isBulk && sectionModal.txn && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm"><strong>Party:</strong> {sectionModal.txn.party_name}</p>
              <p className="text-sm"><strong>Current Rate:</strong> {sectionModal.txn.tds_rate?.toFixed(2)}%</p>
              <p className="text-sm"><strong>Auto-detected Section:</strong> {sectionModal.txn.section || "None"}</p>
            </div>
          )}

          {sectionModal.isBulk && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This will assign the same section to all {selectedTxns.size} selected transactions.
                Expected rates will be calculated individually based on each party's PAN.
              </p>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">Select Section</Label>
            <Select value={newSection} onValueChange={(v) => { setNewSection(v); setRateChoice(""); }}>
              <SelectTrigger className="mt-2" data-testid="section-select-trigger">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                {SECTION_OPTIONS.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {newSection === "194C" && <RateChoice194C rateChoice={rateChoice} setRateChoice={setRateChoice} />}
          {is194I && <RateChoice194I rateChoice={rateChoice} setRateChoice={setRateChoice} />}
          {newSection === "194J" && <RateChoice194J rateChoice={rateChoice} setRateChoice={setRateChoice} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="section-cancel-btn">Cancel</Button>
          <Button onClick={handleSectionUpdate} data-testid="section-confirm-btn">
            <CheckCircle className="w-4 h-4 mr-2" />
            {sectionModal.isBulk ? `Update ${selectedTxns.size} Transactions` : "Update Section"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
